import React, { useState, useEffect, useRef, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Button, Badge,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import { sanitizeEmailHtml } from '../../utils/sanitize';
import {
  ArrowLeft, RefreshCw, Mail, FileText, Package, Users,
  Clock, CheckCircle, AlertCircle, ChevronRight, ChevronDown, ChevronUp,
  ExternalLink, ShoppingCart, Send, Loader2, X, MessageSquare, Brain,
  Inbox, Paperclip, User, Calendar, Building, Eye, Download, BarChart2,
  Edit2, Save
} from 'lucide-react';

// Memoized Reply Textarea with local state to prevent focus loss
const ReplyTextarea = memo(({ onSend, disabled, placeholder, rows = 2 }) => {
  const [localValue, setLocalValue] = useState('');

  const handleSend = useCallback(() => {
    if (localValue.trim()) {
      onSend(localValue.trim());
      setLocalValue('');
    }
  }, [localValue, onSend]);

  return (
    <>
      <textarea
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 resize-none"
        disabled={disabled}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={disabled || !localValue.trim()}
        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
      >
        <Send className="w-4 h-4" />
      </button>
    </>
  );
});

// Memoized Chat Input with local state to prevent focus loss
const AIChatInput = memo(forwardRef(({ onSend, disabled, placeholder }, ref) => {
  const [localValue, setLocalValue] = useState('');

  const handleSend = useCallback(() => {
    if (localValue.trim()) {
      onSend(localValue.trim());
      setLocalValue('');
    }
  }, [localValue, onSend]);

  useImperativeHandle(ref, () => ({
    triggerSend: handleSend
  }), [handleSend]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 focus:ring-2 focus:ring-purple-500"
      autoComplete="off"
      disabled={disabled}
    />
  );
}));

const RFQ_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500' },
  created: { label: 'Created', color: 'bg-blue-500' },
  outreach: { label: 'Outreach', color: 'bg-purple-500' },
  bidding: { label: 'Bidding', color: 'bg-yellow-500' },
  closed: { label: 'Closed', color: 'bg-orange-500' },
  analysis: { label: 'Analysis', color: 'bg-indigo-500' },
  review: { label: 'Review', color: 'bg-pink-500' },
  awarding: { label: 'Awarding', color: 'bg-teal-500' },
  awarded: { label: 'Awarded', color: 'bg-green-500' },
  po_created: { label: 'PO Created', color: 'bg-green-600' },
  completed: { label: 'Completed', color: 'bg-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500' }
};

const RFQWorkflowDetail = ({
  // Workflow mode props
  rfqId: propRfqId,
  runId,
  onWorkflowAction,
  isWorkflowMode = false,
  workflowContext = {},
  loading: workflowLoading = false
}) => {
  // Use prop rfqId in workflow mode, otherwise get from URL params
  const { rfqId: paramRfqId } = useParams();
  const rfqId = propRfqId || paramRfqId;
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [items, setItems] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});

  // Email sending state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [sendTarget, setSendTarget] = useState(null); // null = all vendors, or { conversationId, vendorName, vendorEmail }

  // AI Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const aiChatInputRef = useRef(null);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [aiChatSessionId, setAiChatSessionId] = useState(null);

  // Check Replies Modal state
  const [showRepliesModal, setShowRepliesModal] = useState(false);
  const [selectedVendorForReplies, setSelectedVendorForReplies] = useState(null);
  const [vendorEmails, setVendorEmails] = useState({});
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [activeVendorTab, setActiveVendorTab] = useState(0);
  const [rightPanelTab, setRightPanelTab] = useState('analysis');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyResult, setReplyResult] = useState(null);
  const [draftEmail, setDraftEmail] = useState(null);
  const [analyzingConversation, setAnalyzingConversation] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [refreshingAndAnalyzing, setRefreshingAndAnalyzing] = useState(false); // Prevent multiple clicks
  const [submittingPO, setSubmittingPO] = useState(null); // Track which PO is being submitted
  const [submittingAllPO, setSubmittingAllPO] = useState(false); // Track bulk PO submission

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');

  // PO Creation state
  const [selectedQuotes, setSelectedQuotes] = useState({}); // { item_id: { vendor_id, vendor_name, unit_price, ... } }
  const [creatingPO, setCreatingPO] = useState(false);
  const [poResult, setPoResult] = useState(null);
  const [showPOConfirmModal, setShowPOConfirmModal] = useState(false);

  // EOL (End of Life) Items state
  const [eolItems, setEolItems] = useState({}); // { netsuite_item_id: eol_info }
  const [replacingEOLItem, setReplacingEOLItem] = useState(null);

  // Vendor Form Status
  const [vendorFormStatus, setVendorFormStatus] = useState(null);

  // RFQ Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    est_ship_date: '',
    items: []  // Array of {id, item_name, quantity, netsuite_item_id}
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editResult, setEditResult] = useState(null);

  // Ref to store send reply handler for stable callback
  const sendReplyRef = useRef(null);

  useEffect(() => {
    fetchRFQDetails();
  }, [rfqId]);

  const fetchRFQDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/rfq-workflow/rfq/${rfqId}`);
      console.log('RFQ API Response:', response);
      console.log('Purchase Orders in response:', response?.purchase_orders);

      if (response) {
        setRfq(response.rfq);
        setItems(response.items || []);
        setConversations(response.conversations || []);
        setQuotes(response.quotes || []);
        setPurchaseOrders(response.purchase_orders || []);
        console.log('Set purchaseOrders to:', response.purchase_orders || []);
      }
    } catch (error) {
      console.error('Error fetching RFQ details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch email templates
  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/api/rfq-workflow/templates');
      if (response?.templates) {
        setTemplates(response.templates);
        // Auto-select default template
        const defaultTemplate = response.templates.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate.id);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Open send emails modal (for all vendors)
  const handleOpenSendModal = () => {
    setSendTarget(null);
    setEmailResult(null);
    fetchTemplates();
    setShowTemplateModal(true);
  };

  // Open send emails modal for single vendor
  const handleSendToSingleVendor = (vendor) => {
    setSendTarget({
      conversationId: vendor.conversation_id,
      vendorName: vendor.vendor_name,
      vendorEmail: vendor.vendor_email
    });
    setEmailResult(null);
    fetchTemplates();
    setShowTemplateModal(true);
  };

  // Send emails to vendors
  const handleSendEmails = async () => {
    setSendingEmails(true);
    setEmailResult(null);

    try {
      // Build request payload
      const payload = useCustomEmail
        ? { custom_subject: customSubject, custom_body: customBody }
        : { template_id: selectedTemplate };

      // Add conversation_id for single vendor send
      if (sendTarget?.conversationId) {
        payload.conversation_id = sendTarget.conversationId;
      }

      // In workflow mode, trigger workflow action
      if (isWorkflowMode && onWorkflowAction) {
        await onWorkflowAction('send_emails', payload);
        setShowTemplateModal(false);
        // Data will be refreshed by workflow
        await fetchRFQDetails();
        return;
      }

      // Standalone mode - direct API call
      const response = await apiClient.post(`/api/rfq-workflow/rfq/${rfqId}/send-invites`, payload);

      setEmailResult({
        success: response.success,
        message: sendTarget
          ? (response.success ? `Email sent to ${sendTarget.vendorName}` : response.error || 'Failed to send email')
          : `Sent ${response.sent || 0} of ${response.total || 0} emails`,
        sent: response.sent,
        failed: response.failed
      });

      // Refresh data if emails were sent
      if (response.sent > 0) {
        await fetchRFQDetails();
      }
    } catch (error) {
      setEmailResult({
        success: false,
        message: error.message || 'Failed to send emails'
      });
    } finally {
      setSendingEmails(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowTemplateModal(false);
    setSendTarget(null);
    setEmailResult(null);
    setSelectedTemplate(null);
    setUseCustomEmail(false);
    setCustomSubject('');
    setCustomBody('');
  };

  // ========== RFQ EDIT HANDLERS ==========

  // Open Edit Modal
  const handleOpenEditModal = () => {
    // Pre-populate form with current RFQ data including items
    setEditFormData({
      title: rfq?.title || '',
      start_date: rfq?.start_date ? rfq.start_date.split('T')[0] : '',
      end_date: rfq?.end_date ? rfq.end_date.split('T')[0] : '',
      est_ship_date: rfq?.est_ship_date ? rfq.est_ship_date.split('T')[0] : '',
      items: items.map(item => ({
        id: item.id,
        item_name: item.item_name,
        quantity: item.quantity || 1,
        netsuite_item_id: item.netsuite_item_id
      }))
    });
    setEditResult(null);
    setShowEditModal(true);
  };

  // Close Edit Modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({
      title: '',
      start_date: '',
      end_date: '',
      est_ship_date: '',
      items: []
    });
    setEditResult(null);
  };

  // Handle Edit Form Field Change
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Item Quantity Change - store raw value, convert on save
  const handleItemQuantityChange = (itemId, newQuantity) => {
    // Only update if the value is a valid number or non-empty
    const numValue = parseInt(newQuantity);
    if (newQuantity === '' || isNaN(numValue) || numValue < 1) {
      return; // Ignore empty or invalid values
    }
    setEditFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId
          ? { ...item, quantity: numValue }
          : item
      )
    }));
  };

  // Save RFQ Edit
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    setEditResult(null);

    try {
      // Build payload with only changed fields
      const payload = {};
      if (editFormData.title && editFormData.title !== rfq?.title) {
        payload.title = editFormData.title;
      }
      if (editFormData.start_date) {
        payload.start_date = editFormData.start_date;
      }
      if (editFormData.end_date) {
        payload.end_date = editFormData.end_date;
      }
      if (editFormData.est_ship_date) {
        payload.est_ship_date = editFormData.est_ship_date;
      }

      // Check if any item quantities have changed
      const changedItems = editFormData.items.filter(editItem => {
        const originalItem = items.find(i => i.id === editItem.id);
        const newQty = parseInt(editItem.quantity) || 0;
        return originalItem && newQty !== originalItem.quantity;
      });

      if (changedItems.length > 0) {
        // Include all items with their current quantities (for NetSuite sync)
        payload.items = editFormData.items.map(item => ({
          id: item.id,
          item_name: item.item_name,
          quantity: parseInt(item.quantity) || 1,
          netsuite_item_id: item.netsuite_item_id
        }));
      }

      // Check if there are any changes
      if (Object.keys(payload).length === 0) {
        setEditResult({
          success: false,
          message: 'No changes to save'
        });
        return;
      }

      const response = await apiClient.put(`/api/rfq-workflow/rfq/${rfqId}`, payload);

      if (response.success) {
        setEditResult({
          success: true,
          message: response.netsuite_updated
            ? 'RFQ updated successfully in database and NetSuite'
            : 'RFQ updated successfully in database'
        });
        // Refresh RFQ details
        await fetchRFQDetails();
        // Close modal after short delay to show success message
        setTimeout(() => {
          handleCloseEditModal();
        }, 1500);
      } else {
        setEditResult({
          success: false,
          message: response.error || 'Failed to update RFQ'
        });
      }
    } catch (error) {
      setEditResult({
        success: false,
        message: error.message || 'Failed to update RFQ'
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // AI Chat - Send message through workflow
  const handleAiChat = async () => {
    if (!aiChatInput.trim()) return;

    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiChatLoading(true);

    try {
      // Build context for AI
      const context = {
        rfq_number: rfq?.rfq_number,
        rfq_title: rfq?.title,
        rfq_status: rfq?.status,
        items: items.map(item => ({
          name: item.item_name,
          quantity: item.quantity,
          unit: item.unit,
          specifications: item.specifications
        })),
        vendors: conversations.map(c => ({
          vendor_name: c.vendor_name,
          vendor_email: c.vendor_email,
          status: c.status
        })),
        quotes: quotes
      };

      // Call workflow-based AI chat endpoint
      const response = await apiClient.post(`/api/rfq-workflow/rfq/${rfqId}/chat`, {
        question: userMessage,
        context: context,
        run_id: runId
      });

      if (response.success && response.answer) {
        setAiChatMessages(prev => [...prev, { role: 'assistant', content: response.answer }]);
      } else {
        setAiChatMessages(prev => [...prev, {
          role: 'assistant',
          content: response.error || 'Sorry, I could not process your request.'
        }]);
      }
    } catch (error) {
      setAiChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message || 'Failed to get AI response'}`
      }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // Get selected template object
  const getSelectedTemplateData = () => {
    return templates.find(t => t.id === selectedTemplate);
  };

  // ========== CHECK REPLIES MODAL HANDLERS ==========

  // Open Check Replies Modal (with optional auto-analysis like Normal RFQ)
  // NOTE: autoAnalyze defaults to FALSE like Normal RFQ - user clicks "Refresh & Analyze" to trigger
  const handleOpenRepliesModal = async (vendor = null, autoAnalyze = false) => {
    setSelectedVendorForReplies(vendor);
    setShowRepliesModal(true);

    // Set activeVendorTab to the clicked vendor's index, not always 0
    let targetVendorIndex = 0;
    if (vendor) {
      const vendorIndex = conversations.findIndex(c => c.conversation_id === vendor.conversation_id);
      targetVendorIndex = vendorIndex >= 0 ? vendorIndex : 0;
      setActiveVendorTab(targetVendorIndex);
    } else {
      setActiveVendorTab(0);
    }

    setRightPanelTab('analysis');
    setAiChatMessages([]);
    setReplyText('');
    setReplyResult(null);
    setDraftEmail(null);
    setVendorFormStatus(null);
    setLoadingEmails(true);

    // SEQUENTIAL: First sync new emails from MS Graph to database, THEN load from database
    // Running in parallel caused emails to load BEFORE sync completed
    try {
      await apiClient.post(`/api/rfq-workflow/check-replies/${rfqId}`);
    } catch (err) {
      console.warn('Check replies sync failed:', err);
    }

    // Now load emails from database (after sync)
    await loadAllVendorEmails();

    // Load form status for the active vendor
    const targetConv = vendor || conversations[0];
    if (targetConv?.conversation_id) {
      loadVendorFormStatus(targetConv.conversation_id);
    }

    // Auto-trigger AI analysis if requested (like Normal RFQ)
    if (autoAnalyze) {
      // Small delay to let UI update first
      setTimeout(() => {
        handleAnalyzeEmails();
      }, 500);
    }
  };

  // Open Replies Modal for a SINGLE vendor (from per-vendor Replies button)
  // Only refreshes and analyzes that specific vendor, not all vendors
  const handleOpenSingleVendorReplies = async (vendor) => {
    if (!vendor) return;

    setSelectedVendorForReplies(vendor);
    setShowRepliesModal(true);

    // Set activeVendorTab to the clicked vendor's index
    const vendorIndex = conversations.findIndex(c => c.conversation_id === vendor.conversation_id);
    setActiveVendorTab(vendorIndex >= 0 ? vendorIndex : 0);

    setRightPanelTab('analysis');
    setAiChatMessages([]);
    setReplyText('');
    setReplyResult(null);
    setDraftEmail(null);
    setVendorFormStatus(null);
    setLoadingEmails(true);

    // Load form status for this vendor
    loadVendorFormStatus(vendor.conversation_id);

    // Load emails for this vendor
    try {
      const emailsResponse = await apiClient.get(`/api/rfq-workflow/conversation/${vendor.conversation_id}/emails`);
      if (emailsResponse?.emails) {
        setVendorEmails(prev => ({
          ...prev,
          [vendor.conversation_id]: emailsResponse.emails
        }));
      }
    } catch (err) {
      console.error(`Error loading emails for ${vendor.vendor_name}:`, err);
    } finally {
      setLoadingEmails(false);
    }

    // Trigger analyze using existing handler (has its own loading state)
    handleAnalyzeEmails();
  };

  // Close Check Replies Modal
  const handleCloseRepliesModal = () => {
    setShowRepliesModal(false);
    setSelectedVendorForReplies(null);
    setVendorEmails({});
    setAiChatMessages([]);
    setReplyText('');
    setReplyResult(null);
    setDraftEmail(null);
  };

  // Load emails for all vendors in parallel for faster loading
  // silent=true means don't show loading state (for background refreshes)
  const loadAllVendorEmails = async (silent = false) => {
    if (!silent) {
      setLoadingEmails(true);
    }

    try {
      // Fetch all vendor emails in parallel
      const results = await Promise.all(
        conversations.map(async (conv) => {
          try {
            const response = await apiClient.get(`/api/rfq-workflow/conversation/${conv.conversation_id}/emails`);
            return { conversation_id: conv.conversation_id, emails: response?.emails || [] };
          } catch (err) {
            console.error(`Error loading emails for ${conv.vendor_name}:`, err);
            return { conversation_id: conv.conversation_id, emails: [] };
          }
        })
      );

      // Build emailsData from parallel results
      const emailsData = {};
      results.forEach(result => {
        emailsData[result.conversation_id] = result.emails;
      });

      setVendorEmails(emailsData);
    } finally {
      if (!silent) {
        setLoadingEmails(false);
      }
    }
  };

  // Get current vendor for replies modal
  const getCurrentVendorForReplies = () => {
    if (selectedVendorForReplies) {
      return conversations.find(c => c.conversation_id === selectedVendorForReplies.conversation_id);
    }
    return conversations[activeVendorTab] || null;
  };

  // Refresh current vendor emails only (like Normal RFQ - no full modal reload)
  const refreshCurrentVendorEmails = async () => {
    const currentVendor = getCurrentVendorForReplies();
    if (!currentVendor) return;

    try {
      const response = await apiClient.get(`/api/rfq-workflow/conversation/${currentVendor.conversation_id}/emails`);
      if (response?.emails) {
        setVendorEmails(prev => ({
          ...prev,
          [currentVendor.conversation_id]: response.emails
        }));
      }
    } catch (err) {
      console.error(`Error refreshing emails for ${currentVendor.vendor_name}:`, err);
    }
  };

  // Helper to optimistically add sent email to UI (avoids MS Graph indexing delay)
  const addOptimisticSentEmail = (conversationId, bodyText, subject) => {
    const optimisticEmail = {
      id: `optimistic-${Date.now()}`,
      subject: subject || `RE: RFQ ${rfq?.rfq_number || ''} - ${rfq?.title || ''}`,
      body: bodyText,
      body_type: 'text',
      direction: 'sent',
      received_at: new Date().toISOString(),
      has_attachments: false,
      sender: 'You',
      is_optimistic: true // Flag for deduplication later
    };

    setVendorEmails(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), optimisticEmail]
    }));
  };

  // Send reply to vendor
  const handleSendReply = async () => {
    const currentVendor = getCurrentVendorForReplies();
    if (!replyText.trim() || !currentVendor) return;

    const sentText = replyText.trim();
    setSendingReply(true);
    setReplyResult(null);

    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${currentVendor.conversation_id}/reply`, {
        reply_body: sentText
      });

      if (response.success) {
        setReplyResult({ success: true, message: `Reply sent to ${currentVendor.vendor_name}` });
        setReplyText('');

        // Optimistically add sent email to UI immediately (MS Graph has indexing delay)
        addOptimisticSentEmail(currentVendor.conversation_id, sentText);

        // Background refresh to sync with MS Graph (dedupes optimistic email)
        setTimeout(async () => {
          await refreshCurrentVendorEmails();
          setReplyResult(null);
        }, 3000);
      } else {
        setReplyResult({ success: false, message: response.error || 'Failed to send reply' });
      }
    } catch (error) {
      setReplyResult({ success: false, message: error.message || 'Failed to send reply' });
    } finally {
      setSendingReply(false);
    }
  };

  // Send reply with text parameter - stored in ref for stable callback
  const handleSendReplyWithText = useCallback(async (replyTextParam) => {
    const currentVendor = getCurrentVendorForReplies();
    if (!replyTextParam || !currentVendor) return;

    console.log('[RFQ-Workflow] handleSendReplyWithText called with:', replyTextParam);

    setSendingReply(true);
    setReplyResult(null);

    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${currentVendor.conversation_id}/reply`, {
        reply_body: replyTextParam
      });

      if (response.success) {
        setReplyResult({ success: true, message: `Reply sent to ${currentVendor.vendor_name}` });

        // Optimistically add sent email to UI immediately (MS Graph has indexing delay)
        addOptimisticSentEmail(currentVendor.conversation_id, replyTextParam);

        // Background refresh to sync with MS Graph (dedupes optimistic email)
        setTimeout(async () => {
          await refreshCurrentVendorEmails();
          setReplyResult(null);
        }, 3000);
      } else {
        setReplyResult({ success: false, message: response.error || 'Failed to send reply' });
      }
    } catch (error) {
      setReplyResult({ success: false, message: error.message || 'Failed to send reply' });
    } finally {
      setSendingReply(false);
    }
  }, [conversations, activeVendorTab, rfq]);

  // Keep ref updated with latest handler
  sendReplyRef.current = handleSendReplyWithText;

  // Stable callback for memoized component - never changes reference
  const stableSendReply = useCallback((text) => {
    if (sendReplyRef.current) {
      sendReplyRef.current(text);
    }
  }, []);

  // Send draft email
  const handleSendDraft = async () => {
    if (!draftEmail) return;
    const currentVendor = getCurrentVendorForReplies();
    if (!currentVendor) return;

    const draftContent = draftEmail.content;
    setSendingReply(true);
    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${currentVendor.conversation_id}/reply`, {
        reply_body: draftContent
      });

      if (response.success) {
        setReplyResult({ success: true, message: `Reply sent to ${currentVendor.vendor_name}` });
        setDraftEmail(null);

        // Optimistically add sent email to UI immediately (MS Graph has indexing delay)
        addOptimisticSentEmail(currentVendor.conversation_id, draftContent);

        // Background refresh to sync with MS Graph (dedupes optimistic email)
        setTimeout(async () => {
          await refreshCurrentVendorEmails();
          setReplyResult(null);
        }, 3000);
      } else {
        alert('Failed to send: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to send: ' + error.message);
    } finally {
      setSendingReply(false);
    }
  };

  // Analyze vendor emails
  const handleAnalyzeEmails = async () => {
    const currentVendor = getCurrentVendorForReplies();
    if (!currentVendor) return;

    setAnalyzingConversation(currentVendor.conversation_id);
    setAnalysisResult(null);

    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${currentVendor.conversation_id}/analyze`, {
        analyze_all: true
      });

      if (response.success) {
        setAnalysisResult({
          success: true,
          message: response.message,
          quotes: response.extracted_quotes
        });

        // Immediately update quotes from extracted data
        if (response.extracted_quotes?.length > 0) {
          setQuotes(prev => {
            const updatedQuotes = [...prev];
            for (const extracted of response.extracted_quotes) {
              const existingIdx = updatedQuotes.findIndex(q =>
                q.item_id === extracted.item_id
              );
              if (existingIdx >= 0) {
                // Update existing quote with all extracted fields
                updatedQuotes[existingIdx] = {
                  ...updatedQuotes[existingIdx],
                  unit_price: extracted.unit_price ?? updatedQuotes[existingIdx].unit_price,
                  total_price: extracted.total_price ?? updatedQuotes[existingIdx].total_price,
                  quantity_offered: extracted.quantity_offered ?? extracted.quantity ?? updatedQuotes[existingIdx].quantity_offered,
                  delivery_days: extracted.delivery_days ?? updatedQuotes[existingIdx].delivery_days,
                  estimated_ship_date: extracted.estimated_ship_date ?? updatedQuotes[existingIdx].estimated_ship_date,
                  lead_time: extracted.lead_time ?? updatedQuotes[existingIdx].lead_time,
                  status: 'quoted'
                };
              } else {
                // Add new quote with all extracted fields
                updatedQuotes.push({
                  item_id: extracted.item_id,
                  item_name: extracted.item_name,
                  unit_price: extracted.unit_price,
                  total_price: extracted.total_price,
                  quantity_offered: extracted.quantity_offered ?? extracted.quantity,
                  delivery_days: extracted.delivery_days,
                  estimated_ship_date: extracted.estimated_ship_date,
                  lead_time: extracted.lead_time,
                  vendor_name: currentVendor.vendor_name,
                  vendor_id: currentVendor.netsuite_vendor_id || currentVendor.vendor_email,
                  status: 'quoted'
                });
              }
            }
            return updatedQuotes;
          });
        }

        // Clear analysis result message after delay
        setTimeout(() => {
          setAnalysisResult(null);
        }, 3000);
      } else {
        setAnalysisResult({ success: false, message: response.error || 'Failed to analyze emails' });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysisResult({ success: false, message: error.message || 'Failed to analyze emails' });
    } finally {
      setAnalyzingConversation(null);
    }
  };

  // Check EOL items for the current RFQ items
  const checkEOLItems = async () => {
    if (!items || items.length === 0) return;

    try {
      const netsuiteItemIds = items
        .map(item => item.netsuite_item_id)
        .filter(id => id);

      if (netsuiteItemIds.length === 0) return;

      const response = await apiClient.post('/api/rfq-workflow/check-eol-items', {
        netsuite_item_ids: netsuiteItemIds
      });

      const eolMap = {};
      if (response.eol_items && response.eol_items.length > 0) {
        response.eol_items.forEach(eol => {
          eolMap[eol.eol_netsuite_item_id] = eol;
        });
      }
      setEolItems(eolMap);
    } catch (error) {
      console.debug('EOL check error:', error);
    }
  };

  // Handle replacing an EOL item with its replacement
  const handleReplaceEOLItem = async (item, eolInfo) => {
    if (!item?.id || !eolInfo?.eol_netsuite_item_id) return;

    if (!window.confirm(
      `Replace "${item.item_name}" with "${eolInfo.replacement_item_name}"?\n\n` +
      `This will update the item in this RFQ.`
    )) {
      return;
    }

    setReplacingEOLItem(item.id);

    try {
      const response = await apiClient.post('/api/rfq-workflow/replace-eol-item', {
        rfq_item_id: item.id,
        eol_netsuite_item_id: eolInfo.eol_netsuite_item_id
      });

      if (response.success) {
        // Remove EOL warning for the old item
        setEolItems(prev => {
          const updated = { ...prev };
          delete updated[eolInfo.eol_netsuite_item_id];
          return updated;
        });

        // Reload RFQ data to get updated items
        await fetchRFQDetails();

        alert(`Item replaced successfully!\n\nOld: ${response.old_item.name}\nNew: ${response.new_item.name}`);
      } else {
        alert(`Failed to replace item: ${response.detail || response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error replacing EOL item:', error);
      alert(`Error replacing item: ${error.message}`);
    } finally {
      setReplacingEOLItem(null);
    }
  };

  // Check EOL items when items are loaded
  useEffect(() => {
    if (items && items.length > 0) {
      checkEOLItems();
    }
  }, [items]);

  // AI Chat for replies modal (with tool calling support like Normal RFQ)
  const handleRepliesAiChat = async (messageText) => {
    const currentVendor = getCurrentVendorForReplies();
    if (!messageText || !currentVendor) return;

    const userMessage = messageText;
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiChatLoading(true);
    setDraftEmail(null);

    try {
      // Build conversation history for context (like Normal RFQ)
      const conversationHistory = aiChatMessages.slice(-6).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await apiClient.post(`/api/rfq-workflow/rfq/${rfqId}/chat`, {
        question: userMessage,
        conversation_id: currentVendor.conversation_id,
        conversation_history: conversationHistory,
        run_id: runId
      });

      if (response.success && response.answer) {
        // Build assistant message with metadata (like Normal RFQ)
        const assistantMessage = {
          role: 'assistant',
          content: response.answer,
          draft_email: response.draft_email,
          action_type: response.action_type,
          tool_results: response.tool_results,
          isDraft: response.draft_email != null
        };

        setAiChatMessages(prev => [...prev, assistantMessage]);

        // Handle draft email from tool call
        if (response.draft_email) {
          setDraftEmail({
            conversation_id: currentVendor.conversation_id,
            vendor_name: currentVendor.vendor_name,
            content: response.draft_email.body_html || response.draft_email.body || response.answer,
            subject: response.draft_email.subject
          });
        }

        // Handle action results (like Normal RFQ)
        if (response.tool_results?.length > 0) {
          for (const result of response.tool_results) {
            if (result.tool === 'send_email' && result.result?.success) {
              // Optimistically add sent email to UI (MS Graph has indexing delay)
              // Get email body from draft or from tool result
              const sentBody = draftEmail?.content || response.draft_email?.body_html || response.draft_email?.body || 'Email sent';
              addOptimisticSentEmail(currentVendor.conversation_id, sentBody);
              setDraftEmail(null); // Clear draft after successful send

              // Background refresh to sync with MS Graph
              setTimeout(async () => {
                await refreshCurrentVendorEmails();
              }, 3000);
            }
            if (result.tool === 'update_rfq_quantity' && result.result?.success) {
              // Quantity updated in DB + NetSuite. AI will draft notification email for user to confirm.
              console.log('Quantity updated, AI will draft notification email for confirmation');
            }
            if (result.tool === 'update_line_item_quote' && result.result?.success) {
              // Refresh quotes - handle updated_items array from backend
              const updatedItems = result.result.updated_items || [];
              if (updatedItems.length > 0) {
                setQuotes(prev => {
                  const updatedQuotes = [...prev];
                  for (const quoteUpdate of updatedItems) {
                    const existingIdx = updatedQuotes.findIndex(q =>
                      q.item_id === quoteUpdate.item_id && q.vendor_id === quoteUpdate.vendor_id
                    );
                    if (existingIdx >= 0) {
                      updatedQuotes[existingIdx] = {
                        ...updatedQuotes[existingIdx],
                        unit_price: quoteUpdate.unit_price,
                        total_price: quoteUpdate.total_price
                      };
                    }
                  }
                  return updatedQuotes;
                });
              }
            }
          }
        }
      } else {
        setAiChatMessages(prev => [...prev, {
          role: 'assistant',
          content: response.error || 'Sorry, I could not process your request.'
        }]);
      }
    } catch (error) {
      setAiChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message || 'Failed to get AI response'}`
      }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // Confirm and send drafted email via AI tool (like Normal RFQ)
  const handleConfirmSendEmail = async () => {
    if (!draftEmail) return;

    const currentVendor = getCurrentVendorForReplies();
    if (!currentVendor) return;

    setAiChatLoading(true);

    try {
      // Send confirmation message to AI to trigger send_email tool
      const conversationHistory = aiChatMessages.slice(-8).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Include draft content so AI knows to use send_email tool with this exact body
      const sendQuestion = `Please use the send_email tool to send this email now. Subject: "${draftEmail.subject || ''}". Body: ${draftEmail.content || ''}`;

      const response = await apiClient.post(`/api/rfq-workflow/rfq/${rfqId}/chat`, {
        question: sendQuestion,
        conversation_id: currentVendor.conversation_id,
        conversation_history: conversationHistory,
        run_id: runId
      });

      if (response.success) {
        setAiChatMessages(prev => [...prev,
          { role: 'user', content: 'Yes, please send the email now.' },
          { role: 'assistant', content: response.answer, action_type: response.action_type }
        ]);

        // Check if email was sent
        if (response.tool_results?.some(r => r.tool === 'send_email' && r.result?.success)) {
          // Optimistically add sent email to UI (MS Graph has indexing delay)
          const sentBody = draftEmail?.content || 'Email sent';
          addOptimisticSentEmail(currentVendor.conversation_id, sentBody);
          setDraftEmail(null);

          // Background refresh to sync with MS Graph
          setTimeout(async () => {
            await refreshCurrentVendorEmails();
          }, 3000);
        }
      }
    } catch (error) {
      setAiChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error sending email: ${error.message}`
      }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // Load vendor form submission status
  const loadVendorFormStatus = async (convId) => {
    if (!convId) return;
    try {
      const response = await apiClient.get(`/api/rfq-workflow/vendor-form-status/${convId}`);
      // apiClient interceptor already unwraps response.data
      const formStatus = response?.has_form ? response.form_status : null;
      setVendorFormStatus(formStatus);
      // If form was submitted, refresh quotes data so right panel shows the quote details
      if (formStatus?.is_submitted) {
        fetchRFQDetails();
      }
    } catch (err) {
      console.warn('Failed to load vendor form status:', err);
      setVendorFormStatus(null);
    }
  };

  // Load chat history for current vendor (like Normal RFQ)
  const loadChatHistory = async (convId = null) => {
    const currentVendor = getCurrentVendorForReplies();
    const conversationId = convId || currentVendor?.conversation_id;
    if (!conversationId || !rfqId) return;

    setLoadingChatHistory(true);
    try {
      const response = await apiClient.get(`/api/rfq-workflow/conversation/${conversationId}/chat-history`);
      if (response?.success && response?.messages) {
        setAiChatMessages(response.messages.map(msg => {
          let content = msg.content;
          let actionType = msg.action_type;
          // Parse JSON responses stored in DB (AI returns JSON format)
          if (typeof content === 'string' && content.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(content.trim());
              if (parsed.response) content = parsed.response;
              if (parsed.action_type && parsed.action_type !== 'none') actionType = parsed.action_type;
            } catch (e) { /* not JSON, use as-is */ }
          }
          return {
            role: msg.role === 'user' ? 'user' : 'assistant',
            content,
            draft_email: msg.draft_email,
            action_type: actionType,
            timestamp: msg.timestamp,
            isDraft: msg.draft_email != null
          };
        }));
        if (response.chat_session_id) {
          setAiChatSessionId(response.chat_session_id);
        }
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setLoadingChatHistory(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // View PDF attachment
  const handleViewAttachment = async (email, attachment) => {
    setPdfLoading(true);
    setPdfFilename(attachment.name);
    setPdfViewerOpen(true);

    try {
      const currentVendor = conversations[activeVendorTab];
      const convId = currentVendor?.conversation_id;

      const response = await apiClient.get(
        `/api/rfq-workflow/conversation/${convId}/attachments/${email.id}/${attachment.id}`
      );

      if (response.success && response.attachment) {
        setPdfData(response.attachment.content_base64);
      } else {
        console.error('Failed to fetch attachment:', response.error);
        setPdfData(null);
      }
    } catch (error) {
      console.error('Error fetching attachment:', error);
      setPdfData(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (email, attachment) => {
    try {
      const currentVendor = conversations[activeVendorTab];
      const convId = currentVendor?.conversation_id;

      const response = await apiClient.get(
        `/api/rfq-workflow/conversation/${convId}/attachments/${email.id}/${attachment.id}`
      );

      if (response.success && response.attachment) {
        // Create download link
        const link = document.createElement('a');
        link.href = `data:${response.attachment.content_type};base64,${response.attachment.content_base64}`;
        link.download = response.attachment.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  // Toggle item expanded state
  const toggleItemExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Get vendors for a specific item (filtered by item_ids - same as normal RFQ)
  const getVendorsForItem = (itemId) => {
    return conversations.filter(conv => {
      const itemIds = conv.item_ids || [];
      // item_ids might be string (JSON) or already parsed array
      const parsedItemIds = typeof itemIds === 'string' ? JSON.parse(itemIds) : itemIds;
      return parsedItemIds.includes(itemId);
    });
  };

  // Get quote for a specific item and vendor
  const getQuoteForItemVendor = (itemId, vendorId, conversationId = null) => {
    return quotes.find(q => {
      // Match item
      if (q.item_id !== itemId) return false;

      // Match vendor by conversation_id (most reliable), vendor_id, or netsuite_vendor_id
      if (conversationId && q.conversation_id === conversationId) return true;
      if (q.vendor_id === vendorId || q.netsuite_vendor_id === vendorId) return true;

      return false;
    });
  };

  // Count pending vendors
  const getPendingVendorCount = () => {
    return conversations.filter(c => c.status === 'pending').length;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Handle quote selection for PO creation
  const handleQuoteSelect = (quote) => {
    const itemId = quote.item_id;
    setSelectedQuotes(prev => {
      const newSelections = { ...prev };
      // If already selected, deselect
      if (prev[itemId]?.vendor_id === quote.vendor_id) {
        delete newSelections[itemId];
      } else {
        // Select this quote
        const itemData = items.find(i => i.id === itemId || i.item_id === itemId);
        const qty = quote.quantity_offered || quote.quantity || itemData?.quantity || 1;
        newSelections[itemId] = {
          vendor_id: quote.vendor_id,
          vendor_name: quote.vendor_name,
          unit_price: quote.unit_price,
          quantity: qty,
          total_price: quote.total_price || (quote.unit_price * qty),
          item_id: quote.item_id,
          item_name: quote.item_name || itemData?.item_name,
          quote_id: quote.quote_id
        };
      }
      return newSelections;
    });
  };

  // Check if a quote is selected
  const isQuoteSelected = (itemId, vendorId) => {
    return selectedQuotes[itemId]?.vendor_id === vendorId;
  };

  // Handle PO creation through workflow
  const handleCreatePO = async () => {
    const selectedCount = Object.keys(selectedQuotes).length;
    if (selectedCount === 0) {
      alert('Please select at least one vendor quote for each item');
      return;
    }

    setShowPOConfirmModal(true);
  };

  // Execute PO creation - same flow as Normal RFQ
  const executeCreatePO = async () => {
    const selections = Object.values(selectedQuotes);
    if (selections.length === 0) {
      setPoResult({ success: false, message: 'Please select at least one vendor for an item' });
      return;
    }

    setCreatingPO(true);
    setPoResult(null);

    try {
      // If in workflow mode, use workflow action
      if (isWorkflowMode && onWorkflowAction) {
        const vendorSelections = {};
        selections.forEach(selection => {
          const vendorId = selection.vendor_id;
          if (!vendorSelections[vendorId]) {
            vendorSelections[vendorId] = {
              vendor_id: vendorId,
              vendor_name: selection.vendor_name,
              items: []
            };
          }
          vendorSelections[vendorId].items.push({
            item_id: selection.item_id,
            item_name: selection.item_name,
            quantity: selection.quantity,
            unit_price: selection.unit_price,
            total_price: selection.total_price,
            quote_id: selection.quote_id
          });
        });

        await onWorkflowAction('create_po', {
          rfq_id: rfqId,
          vendor_selections: Object.values(vendorSelections),
          run_id: runId
        });
        setPoResult({ success: true, message: 'PO creation initiated via workflow' });
      } else {
        // Use rfq-workflow generate-po endpoint
        const vendorSelections = {};
        selections.forEach(selection => {
          const vendorId = selection.vendor_id;
          if (!vendorSelections[vendorId]) {
            vendorSelections[vendorId] = {
              vendor_id: vendorId,
              vendor_name: selection.vendor_name,
              items: []
            };
          }
          vendorSelections[vendorId].items.push({
            item_id: selection.item_id,
            item_name: selection.item_name,
            quantity: selection.quantity,
            unit_price: selection.unit_price,
            total_price: selection.total_price,
            quote_id: selection.quote_id
          });
        });

        const response = await apiClient.post(`/api/rfq-workflow/generate-po`, {
          rfq_id: rfqId,
          vendor_selections: Object.values(vendorSelections)
        });

        if (response?.success) {
          setPoResult({
            success: true,
            message: `Successfully created ${response.po_count || 1} Purchase Order(s)`,
            po_ids: response.po_ids
          });
          setShowPOConfirmModal(false);
          setSelectedQuotes({});
          // Refresh to see the new POs
          await fetchRFQDetails();
        } else {
          setPoResult({
            success: false,
            message: response?.error || response?.detail || 'Failed to create PO'
          });
        }
      }
    } catch (error) {
      console.error('Error creating PO:', error);
      setPoResult({
        success: false,
        message: error.response?.data?.detail || error.message || 'Error creating PO'
      });
    } finally {
      setCreatingPO(false);
      setShowPOConfirmModal(false);
    }
  };

  // Submit all draft POs to NetSuite at once
  const handleSubmitAllPO = async () => {
    const draftPOs = purchaseOrders.filter(po =>
      po.status === 'draft' || po.netsuite_po_id === 'PENDING' || !po.netsuite_po_id
    );

    if (draftPOs.length === 0) {
      alert('No draft POs to submit');
      return;
    }

    if (!window.confirm(`Are you sure you want to submit ${draftPOs.length} PO(s) to NetSuite?`)) {
      return;
    }

    setSubmittingAllPO(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      // Submit each draft PO sequentially
      for (const po of draftPOs) {
        try {
          const response = await apiClient.post(`/api/rfq-workflow/submit-po/${po.id}`);
          if (response?.success) {
            successCount++;
          } else {
            failCount++;
            errors.push(`${po.po_number}: ${response?.error || 'Unknown error'}`);
          }
        } catch (err) {
          failCount++;
          errors.push(`${po.po_number}: ${err.message}`);
        }
      }

      // Show result
      if (failCount === 0) {
        alert(`Successfully submitted ${successCount} PO(s) to NetSuite!`);
      } else {
        alert(`Submitted ${successCount} PO(s), ${failCount} failed.\n\nErrors:\n${errors.join('\n')}`);
      }

      // Refresh to see updated POs
      await fetchRFQDetails();
    } catch (error) {
      alert('Error submitting POs: ' + error.message);
    } finally {
      setSubmittingAllPO(false);
    }
  };

  // Calculate PO summary
  const getPOSummary = () => {
    const selections = Object.values(selectedQuotes);
    const totalItems = selections.length;
    const totalAmount = selections.reduce((sum, s) => sum + (s.total_price || 0), 0);
    const vendors = [...new Set(selections.map(s => s.vendor_name))];
    return { totalItems, totalAmount, vendors };
  };

  const getStatusBadge = (status) => {
    const config = RFQ_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-500' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color} text-white`}>
        {config.label}
      </span>
    );
  };

  const getConversationStatusCounts = () => {
    const counts = {
      total: conversations.length,
      responded: conversations.filter(c => ['responded', 'quoted'].includes(c.status)).length,
      awaiting: conversations.filter(c => ['email_sent', 'awaiting_response'].includes(c.status)).length
    };
    return counts;
  };

  const convStats = getConversationStatusCounts();

  // Wrapper component - MainLayout for standalone, div for workflow mode
  const Wrapper = ({ children, title }) => {
    if (isWorkflowMode) {
      return <div className="w-full">{children}</div>;
    }
    return <MainLayout title={title}>{children}</MainLayout>;
  };

  if (loading) {
    return (
      <Wrapper title="RFQ Details">
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Wrapper>
    );
  }

  if (!rfq) {
    return (
      <Wrapper title="RFQ Details">
        <div className="p-6">
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">RFQ not found</p>
              <Button
                variant="link"
                onClick={() => navigate('/rfq-workflow')}
                className="mt-2"
              >
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper title={`RFQ: ${rfq.rfq_number}`}>
      <div className={isWorkflowMode ? "space-y-6" : "p-6 space-y-6"}>
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/rfq-workflow')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {rfq.rfq_number}
                </h1>
                {getStatusBadge(rfq.status)}
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {rfq.title}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchRFQDetails}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/rfq-workflow/debug?rfqId=${rfqId}`)}
            >
              <FileText className="w-4 h-4 mr-2" />
              Debug Logs
            </Button>
            {getPendingVendorCount() > 0 && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleOpenSendModal}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Emails ({getPendingVendorCount()})
              </Button>
            )}
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => handleOpenRepliesModal(null, true)}
              disabled={workflowLoading}
            >
              <Mail className="w-4 h-4 mr-2" />
              Check Replies
            </Button>
            <Button
              variant="outline"
              className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}/analyze`)}
            >
              <BarChart2 className="w-4 h-4 mr-2" />
              Compare Bids
            </Button>
            {Object.keys(selectedQuotes).length > 0 && (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleCreatePO}
                disabled={creatingPO}
              >
                {creatingPO ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                Create PO ({Object.keys(selectedQuotes).length})
              </Button>
            )}
          </div>
        </div>

        {/* PO Selection Summary */}
        {Object.keys(selectedQuotes).length > 0 && (
          <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <ShoppingCart className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-800 dark:text-purple-300">
                    Selected for PO: {getPOSummary().totalItems} item(s) from {getPOSummary().vendors.length} vendor(s)
                  </span>
                  <span className="text-purple-600 font-semibold">
                    Total: {formatCurrency(getPOSummary().totalAmount)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedQuotes({})}
                    className="text-gray-600"
                  >
                    Clear Selection
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCreatePO}
                    disabled={creatingPO}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {creatingPO ? 'Creating...' : 'Create Purchase Order'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PO Result Message */}
        {poResult && (
          <Card className={`border-${poResult.success ? 'green' : 'red'}-200 bg-${poResult.success ? 'green' : 'red'}-50`}>
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                {poResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`${poResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {poResult.message}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPoResult(null)}
                  className="ml-auto"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchase Orders Section - Show if POs exist */}
        {purchaseOrders.length > 0 && (
          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-green-800 dark:text-green-300">
                  <ShoppingCart className="w-5 h-5" />
                  Purchase Orders Created ({purchaseOrders.length})
                </CardTitle>
                {/* Submit All button - only show if there are draft POs */}
                {purchaseOrders.some(po => po.status === 'draft' || po.netsuite_po_id === 'PENDING' || !po.netsuite_po_id) && (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={submittingAllPO}
                    onClick={handleSubmitAllPO}
                  >
                    {submittingAllPO ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting All...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit All to NetSuite
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {purchaseOrders.map((po) => {
                  const isDraft = po.status === 'draft' || po.netsuite_po_id === 'PENDING' || !po.netsuite_po_id;
                  return (
                    <div key={po.id} className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        className={`bg-white dark:bg-gray-800 border-green-300 ${
                          isDraft
                            ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900'
                            : 'hover:bg-green-100 dark:hover:bg-green-900'
                        }`}
                        onClick={() => {
                          if (!isDraft && po.netsuite_url) {
                            window.open(po.netsuite_url, '_blank');
                          }
                        }}
                        disabled={isDraft}
                      >
                        {isDraft ? (
                          <Clock className="w-4 h-4 mr-2 text-yellow-600" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2 text-green-600" />
                        )}
                        <span className="font-medium">{po.po_number}</span>
                        <span className="ml-2 text-gray-500">({po.vendor_name})</span>
                        <span className="ml-2 text-green-600 font-medium">{formatCurrency(po.total_amount)}</span>
                        {isDraft && (
                          <Badge className="ml-2 bg-yellow-100 text-yellow-800">Draft</Badge>
                        )}
                      </Button>
                      {isDraft && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          disabled={submittingPO === po.id}
                          onClick={async () => {
                            try {
                              setSubmittingPO(po.id);
                              const response = await apiClient.post(`/api/rfq-workflow/submit-po/${po.id}`);
                              if (response?.success) {
                                alert('PO submitted to NetSuite successfully!');
                                fetchRFQDetails(); // Refresh to get updated PO
                              } else {
                                alert('Failed to submit PO: ' + (response?.error || 'Unknown error'));
                              }
                            } catch (err) {
                              alert('Error submitting PO: ' + err.message);
                            } finally {
                              setSubmittingPO(null);
                            }
                          }}
                        >
                          {submittingPO === po.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            'Submit to NetSuite'
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* RFQ Details Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              RFQ Details
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenEditModal}
              className="flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit RFQ
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <User className="w-4 h-4" />
                  Customer
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {rfq.customer_name || rfq.customer_id || '-'}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Building className="w-4 h-4" />
                  Subsidiary
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {rfq.subsidiary_name || rfq.subsidiary_id || '-'}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  Start Date
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(rfq.start_date)}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  End Date
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(rfq.end_date)}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Users className="w-4 h-4" />
                  Vendors
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {conversations.length} vendor{conversations.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <FileText className="w-4 h-4" />
                  Quotes
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {quotes.length} quote{quotes.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items Card with Expandable Vendors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Line Items
            </CardTitle>
            <CardDescription>
              {items.length} item{items.length !== 1 ? 's' : ''} in this RFQ with {conversations.length} vendor{conversations.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {items.length > 0 ? (
              <div className="space-y-4">
                {items.map((item) => {
                  const itemVendors = getVendorsForItem(item.id);
                  const isExpanded = expandedItems[item.id];

                  return (
                    <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      {/* Item Row */}
                      <div
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleItemExpanded(item.id)}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <Package className="w-5 h-5 text-gray-400" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {item.item_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              NetSuite ID: {item.netsuite_item_id}
                            </div>
                          </div>
                          <div className="text-right mr-4">
                            <div className="font-medium text-gray-900 dark:text-white">
                              Qty: {item.quantity}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {item.unit || 'units'}
                            </div>
                          </div>
                          <Badge variant="outline" className="mr-2">
                            <Users className="w-3 h-3 mr-1" />
                            {itemVendors.length} Vendor{itemVendors.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>

                      {/* Expanded Vendor Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          {itemVendors.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Vendor</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Quote</TableHead>
                                  <TableHead className="text-center">Select for PO</TableHead>
                                  <TableHead>Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {itemVendors.map((vendor, idx) => {
                                  const quote = getQuoteForItemVendor(item.id, vendor.netsuite_vendor_id, vendor.conversation_id);
                                  return (
                                    <TableRow key={vendor.conversation_id || vendor.id}>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                            <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                          </div>
                                          <div>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                              {vendor.vendor_name}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              ID: {vendor.netsuite_vendor_id}
                                            </div>
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                                          <Mail className="w-3 h-3" />
                                          {vendor.vendor_email || 'No email'}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                          vendor.status === 'po_created' ? 'bg-indigo-100 text-indigo-800' :
                                          vendor.status === 'quoted' ? 'bg-green-100 text-green-800' :
                                          vendor.status === 'responded' ? 'bg-blue-100 text-blue-800' :
                                          vendor.status === 'sent' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {vendor.status === 'po_created' ? 'PO Created' : vendor.status}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        {quote ? (
                                          <div className="text-green-600 dark:text-green-400 font-medium">
                                            ${quote.unit_price?.toFixed(2) || '-'}
                                            <span className="text-xs text-gray-500 ml-1">/ unit</span>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">No quote</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {vendor.status === 'po_created' ? (
                                          <span className="text-indigo-600 font-medium text-xs">✓ PO</span>
                                        ) : quote ? (
                                          <input
                                            type="radio"
                                            name={`quote-select-${item.id}`}
                                            checked={isQuoteSelected(item.id, vendor.netsuite_vendor_id)}
                                            onChange={() => handleQuoteSelect({
                                              ...quote,
                                              vendor_id: vendor.netsuite_vendor_id,
                                              vendor_name: vendor.vendor_name,
                                              item_id: item.id,
                                              item_name: item.item_name
                                            })}
                                            className="w-4 h-4 text-blue-600 cursor-pointer"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        ) : (
                                          <span className="text-gray-300">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex gap-1">
                                          {vendor.status === 'pending' && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSendToSingleVendor(vendor);
                                              }}
                                              title="Send email to this vendor"
                                            >
                                              <Send className="w-4 h-4 mr-1" />
                                              Send
                                            </Button>
                                          )}
                                          {['sent', 'awaiting_response', 'response_received', 'quoted', 'po_created'].includes(vendor.status) && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-green-600 border-green-600 hover:bg-green-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Use single vendor handler - only refreshes/analyzes THIS vendor
                                                handleOpenSingleVendorReplies(vendor);
                                              }}
                                              title="Check replies from this vendor"
                                            >
                                              <Inbox className="w-4 h-4 mr-1" />
                                              Replies
                                            </Button>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          ) : (
                            <div className="p-4 text-center text-gray-500">
                              No vendors assigned to this item
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No items in this RFQ</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                {sendTarget ? `Send Email to ${sendTarget.vendorName}` : 'Send RFQ to All Vendors'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Vendor Info */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                {sendTarget ? (
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p><strong>Sending to:</strong> {sendTarget.vendorName}</p>
                    <p className="text-xs mt-1">{sendTarget.vendorEmail?.split(';').join(', ')}</p>
                  </div>
                ) : (
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>{getPendingVendorCount()}</strong> vendor{getPendingVendorCount() !== 1 ? 's' : ''} will receive the RFQ invitation email.
                  </p>
                )}
              </div>

              {/* Toggle: Template vs Custom */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="emailType"
                    checked={!useCustomEmail}
                    onChange={() => setUseCustomEmail(false)}
                    className="w-4 h-4 text-blue-600"
                    disabled={sendingEmails}
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Use Template
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="emailType"
                    checked={useCustomEmail}
                    onChange={() => setUseCustomEmail(true)}
                    className="w-4 h-4 text-blue-600"
                    disabled={sendingEmails}
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Custom Email
                  </span>
                </label>
              </div>

              {!useCustomEmail ? (
                <>
                  {/* Template Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Template
                    </label>
                    <select
                      value={selectedTemplate || ''}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      disabled={sendingEmails}
                    >
                      <option value="">Select a template...</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} {template.is_default ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Template Preview */}
                  {selectedTemplate && getSelectedTemplateData() && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Preview</p>
                      </div>
                      <div className="p-3 space-y-3">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subject:</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {getSelectedTemplateData().subject_template}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Body:</p>
                          <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700 max-h-40 overflow-y-auto whitespace-pre-wrap">
                            {getSelectedTemplateData().body_template}
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 italic">
                          Variables like {'{{vendor_name}}'}, {'{{rfq_number}}'}, {'{{items}}'} will be replaced automatically.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Custom Email Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      placeholder="RFQ {{rfq_number}} - Request for Quote"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      disabled={sendingEmails}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Body
                    </label>
                    <textarea
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      placeholder={`Dear {{vendor_name}},

We are requesting quotes for the following items:
{{items}}

Please submit your quote by {{bid_end_date}}.

Best regards,
{{requester_name}}`}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                      disabled={sendingEmails}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Available variables: {'{{vendor_name}}'}, {'{{rfq_number}}'}, {'{{company_name}}'}, {'{{items}}'}, {'{{bid_end_date}}'}, {'{{requester_name}}'}
                  </p>
                </>
              )}

              {/* Result Display */}
              {emailResult && (
                <div className={`rounded-lg p-3 ${
                  emailResult.success
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  <div className="flex items-center gap-2">
                    {emailResult.success ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span>{emailResult.message}</span>
                  </div>
                  {emailResult.failed > 0 && (
                    <p className="text-sm mt-1">
                      {emailResult.failed} email{emailResult.failed !== 1 ? 's' : ''} failed to send
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                disabled={sendingEmails}
              >
                {emailResult?.success ? 'Close' : 'Cancel'}
              </Button>
              {!emailResult?.success && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleSendEmails}
                  disabled={sendingEmails || (!useCustomEmail && !selectedTemplate) || (useCustomEmail && (!customSubject || !customBody))}
                >
                  {sendingEmails ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Emails
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========== EDIT RFQ MODAL ========== */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-600" />
                Edit RFQ Details
              </h3>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* RFQ Number - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RFQ Number
                </label>
                <input
                  type="text"
                  value={rfq?.rfq_number || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => handleEditFormChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={savingEdit}
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bid Start Date
                </label>
                <input
                  type="date"
                  value={editFormData.start_date}
                  onChange={(e) => handleEditFormChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={savingEdit}
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bid End Date
                </label>
                <input
                  type="date"
                  value={editFormData.end_date}
                  onChange={(e) => handleEditFormChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={savingEdit}
                />
              </div>

              {/* Est Ship Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estimated Ship Date
                </label>
                <input
                  type="date"
                  value={editFormData.est_ship_date}
                  onChange={(e) => handleEditFormChange('est_ship_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={savingEdit}
                />
              </div>

              {/* Items with Quantity */}
              {editFormData.items && editFormData.items.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Item Quantities
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                    {editFormData.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate" title={item.item_name}>
                          {item.item_name}
                        </span>
                        <input
                          type="number"
                          min="1"
                          defaultValue={item.quantity}
                          onBlur={(e) => handleItemQuantityChange(item.id, e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          disabled={savingEdit}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Updating quantities will notify all vendors via email
                  </p>
                </div>
              )}

              {/* Result Message */}
              {editResult && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  editResult.success
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {editResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span className="text-sm">{editResult.message}</span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={handleCloseEditModal}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CHECK REPLIES MODAL - Split View ========== */}
      {showRepliesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-4 my-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Inbox className="w-5 h-5 text-green-600" />
                {selectedVendorForReplies
                  ? `Replies from ${selectedVendorForReplies.vendor_name}`
                  : 'Check All Vendor Replies'}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={loadAllVendorEmails}
                  disabled={loadingEmails || analyzingConversation}
                >
                  {loadingEmails ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={async () => {
                    if (refreshingAndAnalyzing) return; // Prevent double-click
                    setRefreshingAndAnalyzing(true);
                    try {
                      // First sync new emails from MS Graph
                      try {
                        await apiClient.post(`/api/rfq-workflow/check-replies/${rfqId}`);
                      } catch (err) {
                        console.warn('Check replies sync failed:', err);
                      }
                      // Then load and analyze
                      await loadAllVendorEmails();
                      await handleAnalyzeEmails();
                    } finally {
                      setRefreshingAndAnalyzing(false);
                    }
                  }}
                  disabled={loadingEmails || analyzingConversation || refreshingAndAnalyzing}
                  title="Sync new emails from Outlook, then run AI analysis to extract quotes"
                >
                  {(analyzingConversation || refreshingAndAnalyzing) ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  {refreshingAndAnalyzing ? 'Analyzing...' : 'Refresh & Analyze'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCloseRepliesModal}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Modal Body - Split View */}
            <div style={{ height: 'calc(90vh - 140px)' }}>
              {loadingEmails ? (
                <div className="flex flex-col items-center justify-center py-12 h-full">
                  <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading vendor conversations...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500 h-full flex items-center justify-center">
                  <div>
                    <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No vendor conversations yet</p>
                  </div>
                </div>
              ) : (
                <div className="flex h-full">
                  {/* ========== LEFT PANEL - Email Thread (60%) ========== */}
                  <div className="w-3/5 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full">
                    {/* Vendor Tabs */}
                    <div className="flex gap-1 overflow-x-auto p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      {conversations.map((conv, idx) => (
                        <button
                          key={conv.conversation_id}
                          onClick={() => {
                            setActiveVendorTab(idx);
                            setSelectedVendorForReplies(null); // Clear to use activeVendorTab
                            setAiChatMessages([]); // Clear current chat
                            setRightPanelTab('analysis');
                            setReplyText('');
                            setDraftEmail(null);
                            // Load new vendor's chat history
                            loadChatHistory(conv.conversation_id);
                            // Load form status for this vendor
                            loadVendorFormStatus(conv.conversation_id);
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 ${
                            activeVendorTab === idx
                              ? 'bg-blue-600 text-white'
                              : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                          }`}
                        >
                          {conv.vendor_name?.substring(0, 15)}{conv.vendor_name?.length > 15 ? '...' : ''}
                          {conv.status === 'responded' && <span className="w-2 h-2 rounded-full bg-green-400" />}
                        </button>
                      ))}
                    </div>

                    {/* Email Thread - Using stable conditional rendering (no IIFE) */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {(vendorEmails[conversations[activeVendorTab]?.conversation_id] || []).length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Mail className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p>No emails in this conversation</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {(vendorEmails[conversations[activeVendorTab]?.conversation_id] || []).map((email, idx) => {
                            const currentVendor = conversations[activeVendorTab];
                            const isFromVendor = email.direction === 'received' ||
                              (email.sender && currentVendor?.vendor_email?.toLowerCase().includes(email.sender?.toLowerCase()));
                            return (
                              <div key={email.id || idx} className={`flex ${isFromVendor ? 'justify-start' : 'justify-end'}`}>
                                <div className={`max-w-[90%] rounded-lg shadow-sm overflow-hidden ${
                                  isFromVendor ? 'bg-green-600' : 'bg-blue-600'
                                }`}>
                                  <div className={`px-3 py-2 text-xs text-white/90 ${
                                    isFromVendor ? 'bg-green-700' : 'bg-blue-700'
                                  }`}>
                                    {isFromVendor
                                      ? `📥 ${currentVendor?.vendor_name}`
                                      : '📤 You'} • {email.received_at ? new Date(email.received_at).toLocaleString() : 'N/A'}
                                    {email.has_attachments && <Paperclip className="w-3 h-3 inline ml-2" />}
                                  </div>
                                  <div className="px-3 py-2 text-sm font-medium text-white">{email.subject}</div>
                                  <div className="bg-white dark:bg-gray-800 p-3 text-sm text-gray-800 dark:text-gray-200">
                                    {email.body_type === 'html' ? (
                                      <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(email.body) }} className="prose prose-sm max-w-none" />
                                    ) : (
                                      <pre className="whitespace-pre-wrap font-sans">{email.body || email.preview}</pre>
                                    )}
                                  </div>

                                  {/* Attachments */}
                                  {email.attachments && email.attachments.length > 0 && (
                                    <div className="bg-white dark:bg-gray-800 px-3 py-2 border-t border-gray-200 dark:border-gray-600">
                                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                        <Paperclip className="w-3 h-3" />
                                        <span>{email.attachments.length} Attachment{email.attachments.length > 1 ? 's' : ''}</span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {email.attachments.map((att, attIdx) => (
                                          <div
                                            key={attIdx}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                                              att.is_pdf
                                                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                                : 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                            }`}
                                          >
                                            <FileText className={`w-4 h-4 ${att.is_pdf ? 'text-red-500' : 'text-gray-500'}`} />
                                            <span className="max-w-[150px] truncate" title={att.name}>
                                              {att.name}
                                            </span>
                                            <span className="text-gray-400">({formatFileSize(att.size)})</span>
                                            {att.is_pdf && (
                                              <button
                                                onClick={() => handleViewAttachment(email, att)}
                                                className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"
                                                title="View PDF"
                                              >
                                                <Eye className="w-3.5 h-3.5 text-red-600" />
                                              </button>
                                            )}
                                            <button
                                              onClick={() => handleDownloadAttachment(email, att)}
                                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                              title="Download"
                                            >
                                              <Download className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Reply Input */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      {replyResult && (
                        <div className={`mb-2 p-2 rounded text-sm ${replyResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {replyResult.message}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <ReplyTextarea
                          onSend={stableSendReply}
                          disabled={sendingReply}
                          placeholder="Type your reply..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ========== RIGHT PANEL - Analysis & AI Chat (40%) ========== */}
                  <div className="w-2/5 flex flex-col h-full">
                    {/* Panel Tabs */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setRightPanelTab('analysis')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                          rightPanelTab === 'analysis'
                            ? 'border-b-2 border-blue-600 text-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        📊 Analysis
                      </button>
                      <button
                        onClick={() => {
                          setRightPanelTab('chat');
                          const currentVendor = conversations[activeVendorTab];
                          const convId = currentVendor?.conversation_id;
                          if (aiChatMessages.length === 0 && convId) {
                            loadChatHistory(convId);
                          }
                        }}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                          rightPanelTab === 'chat'
                            ? 'border-b-2 border-blue-600 text-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        🤖 AI Chat
                      </button>
                    </div>

                    {/* Panel Content */}
                    <div className="flex-1 overflow-y-auto">
                      {rightPanelTab === 'analysis' ? (
                        /* Analysis Tab */
                        <div className="p-4">
                          {/* Vendor Form Status */}
                          {vendorFormStatus && (
                            <div className={`mb-4 p-3 rounded-lg border ${
                              vendorFormStatus.is_submitted
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                            }`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-sm font-medium ${
                                  vendorFormStatus.is_submitted
                                    ? 'text-green-800 dark:text-green-300'
                                    : 'text-blue-800 dark:text-blue-300'
                                }`}>
                                  {vendorFormStatus.is_submitted ? '✅ Quote Form Submitted' : '📋 Quote Form Sent'}
                                </span>
                              </div>
                              {vendorFormStatus.is_submitted ? (
                                <p className="text-xs text-green-700 dark:text-green-400">
                                  Vendor submitted their quote via online form on {new Date(vendorFormStatus.submitted_at).toLocaleString()}
                                </p>
                              ) : (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Online quote form link was sent to {vendorFormStatus.vendor_email}. Awaiting submission.
                                </p>
                              )}
                            </div>
                          )}

                          {/* Quote Extraction */}
                          <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                                🤖 AI Quote Extraction
                              </span>
                              <Button
                                size="sm"
                                onClick={handleAnalyzeEmails}
                                disabled={analyzingConversation !== null || refreshingAndAnalyzing}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                              >
                                {(analyzingConversation || refreshingAndAnalyzing) ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <Brain className="w-3 h-3 mr-1" />
                                    Extract Quotes
                                  </>
                                )}
                              </Button>
                            </div>
                            <p className="text-xs text-purple-600 dark:text-purple-400">
                              Analyze vendor emails to extract pricing
                            </p>
                            {analysisResult && (
                              <div className={`mt-2 p-2 rounded text-xs ${
                                analysisResult.success
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {analysisResult.message}
                              </div>
                            )}
                          </div>

                          {/* RFQ Items & Quotes */}
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">RFQ Items (Assigned to Vendor)</h4>
                          <div className="space-y-2">
                            {(() => {
                              const currentVendor = conversations[activeVendorTab];
                              // Filter items by vendor's assigned item_ids
                              const vendorItemIds = Array.isArray(currentVendor?.item_ids) ? currentVendor.item_ids : [];
                              const vendorItems = items.filter(item => vendorItemIds.includes(item.id));

                              if (vendorItems.length === 0) {
                                return <p className="text-sm text-gray-500">No specific items assigned to this vendor</p>;
                              }

                              return vendorItems.map((item, idx) => {
                              // Match quote by item_id AND (conversation_id OR vendor_id OR vendor_email)
                              const quote = quotes.find(q =>
                                q.item_id === item.id && (
                                  q.conversation_id === currentVendor?.conversation_id ||
                                  q.vendor_id === currentVendor?.netsuite_vendor_id ||
                                  q.vendor_id === currentVendor?.vendor_id ||
                                  q.vendor_id === currentVendor?.vendor_email
                                )
                              );
                              return (
                                <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium text-gray-900 dark:text-white text-sm">{item.item_name}</p>
                                      <p className="text-xs text-gray-500">ID: {item.netsuite_item_id || item.item_id}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-blue-600">{item.quantity} {item.unit || 'units'}</p>
                                    </div>
                                  </div>
                                  {item.specifications && (
                                    <p className="text-xs text-gray-500 mt-1">Specs: {item.specifications}</p>
                                  )}
                                  {quote && (quote.unit_price || quote.total_price) ? (
                                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded space-y-1">
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-green-800 dark:text-green-300">💰 Vendor Quote</span>
                                        <span className="text-xs text-gray-500">
                                          {quote.status === 'accepted' ? '🎉 Accepted' : '✅ Quoted'}
                                        </span>
                                      </div>
                                      <p className="text-xs text-green-800 dark:text-green-300">
                                        <strong>Price:</strong> {quote.unit_price ? `$${quote.unit_price?.toFixed(2)} / unit` : ''}
                                        {quote.unit_price && quote.total_price ? ' | ' : ''}
                                        {quote.total_price ? `Total: $${quote.total_price.toFixed(2)}` : ''}
                                      </p>
                                      {quote.quantity_offered && (
                                        <p className="text-xs text-green-700 dark:text-green-400">
                                          <strong>Qty Offered:</strong> {quote.quantity_offered} {item.unit || 'units'}
                                        </p>
                                      )}
                                      {(quote.estimated_ship_date || quote.delivery_days || quote.lead_time) && (
                                        <p className="text-xs text-green-700 dark:text-green-400">
                                          <strong>Delivery:</strong> {quote.estimated_ship_date || (quote.delivery_days ? `${quote.delivery_days} days` : quote.lead_time)}
                                        </p>
                                      )}
                                      {quote.payment_terms && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                          <strong>Terms:</strong> {quote.payment_terms}
                                        </p>
                                      )}
                                      {quote.quoted_at && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Updated: {new Date(quote.quoted_at).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>
                                  ) : quote ? (
                                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                      <p className="text-xs text-blue-700 dark:text-blue-400">
                                        {quote.status === 'requested' ? '📧 Response received - awaiting price' : '📧 Response received'}
                                      </p>
                                      {quote.quantity_offered && (
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                          Qty mentioned: {quote.quantity_offered} {item.unit || 'units'}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                                      <p className="text-xs text-yellow-700 dark:text-yellow-400">⏳ Awaiting vendor quote</p>
                                    </div>
                                  )}

                                  {/* EOL Warning with Use Replacement Button */}
                                  {eolItems[item.netsuite_item_id] && (
                                    <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded">
                                      <div className="flex items-start gap-2">
                                        <span className="text-amber-600 text-sm">⚠️</span>
                                        <div className="flex-1">
                                          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                                            End of Life Item
                                          </p>
                                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                            {eolItems[item.netsuite_item_id].reason || 'This item is discontinued'}
                                          </p>
                                          {eolItems[item.netsuite_item_id].replacement_item_name && (
                                            <>
                                              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                                <strong>Replacement:</strong> {eolItems[item.netsuite_item_id].replacement_item_name}
                                              </p>
                                              {eolItems[item.netsuite_item_id].replacement_netsuite_item_id && (
                                                <button
                                                  onClick={() => handleReplaceEOLItem(item, eolItems[item.netsuite_item_id])}
                                                  disabled={replacingEOLItem === item.id}
                                                  className="mt-2 px-3 py-1 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
                                                >
                                                  {replacingEOLItem === item.id ? 'Replacing...' : '🔄 Use Replacement Item'}
                                                </button>
                                              )}
                                            </>
                                          )}
                                          {eolItems[item.netsuite_item_id].source_vendor_name && (
                                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                              Reported by: {eolItems[item.netsuite_item_id].source_vendor_name}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                            })()}
                          </div>

                          {/* Vendor Status */}
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <h5 className="font-medium text-blue-800 dark:text-blue-300 text-sm mb-2">Vendor Status</h5>
                            <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                              {conversations[activeVendorTab]?.vendor_name || 'No vendor selected'}
                            </p>
                            {conversations[activeVendorTab]?.vendor_email && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-all">
                                {conversations[activeVendorTab]?.vendor_email}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Emails: {vendorEmails[conversations[activeVendorTab]?.conversation_id]?.length || 0}
                            </p>
                          </div>
                        </div>
                      ) : (
                        /* AI Chat Tab */
                        <div className="flex flex-col h-full">
                          {/* Chat Messages */}
                          <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {loadingChatHistory ? (
                              <div className="text-center py-8">
                                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
                                <p className="text-sm text-gray-500">Loading chat history...</p>
                              </div>
                            ) : aiChatMessages.length === 0 ? (
                              <div className="text-center text-gray-500 py-8">
                                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm font-medium">AI Email Assistant</p>
                                <p className="text-xs mt-2">I can help you:</p>
                                <ul className="text-xs mt-1 space-y-1">
                                  <li>📧 Draft response emails to vendors</li>
                                  <li>📊 Analyze quotes and pricing</li>
                                  <li>💾 Update RFQ quote data</li>
                                  <li>📤 Send emails (with your confirmation)</li>
                                </ul>
                                <p className="text-xs mt-3 italic">Try: "Draft a response asking for better pricing"</p>
                              </div>
                            ) : (
                              aiChatMessages.map((msg, idx) => (
                                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                                    msg.role === 'user'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                  }`}>
                                    <div className="whitespace-pre-wrap">{(() => {
                                      // Parse JSON responses from AI (system prompt returns JSON format)
                                      if (typeof msg.content === 'string' && msg.content.trim().startsWith('{')) {
                                        try {
                                          const parsed = JSON.parse(msg.content.trim());
                                          if (parsed.response) return parsed.response;
                                        } catch (e) { /* not JSON, show as-is */ }
                                      }
                                      return msg.content;
                                    })()}</div>
                                    {/* Show draft email if present */}
                                    {msg.draft_email && (
                                      <div className="mt-2 p-2 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500">
                                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">📧 Draft Email</p>
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                                          Subject: {msg.draft_email.subject}
                                        </p>
                                        <div
                                          className="text-xs mt-1 text-gray-600 dark:text-gray-300 max-h-24 overflow-y-auto"
                                          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(msg.draft_email.body_html) }}
                                        />
                                      </div>
                                    )}
                                    {/* Show action type badge */}
                                    {msg.action_type && msg.action_type !== 'none' && (
                                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${
                                        msg.action_type === 'draft_email' ? 'bg-yellow-200 text-yellow-800' :
                                        msg.action_type === 'send_email' ? 'bg-green-200 text-green-800' :
                                        msg.action_type === 'update_quote' ? 'bg-blue-200 text-blue-800' :
                                        msg.action_type === 'quantity_updated' ? 'bg-purple-200 text-purple-800' :
                                        'bg-gray-200 text-gray-800'
                                      }`}>
                                        {msg.action_type === 'draft_email' ? '📝 Draft Created' :
                                         msg.action_type === 'send_email' ? '✅ Email Sent' :
                                         msg.action_type === 'update_quote' ? '💾 Quotes Updated' :
                                         msg.action_type === 'quantity_updated' ? '📦 Quantity Updated' :
                                         msg.action_type}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                            {aiChatLoading && (
                              <div className="flex justify-start">
                                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                                  <span className="text-xs text-gray-500">AI is thinking...</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Draft Email Confirmation - Tool-based send like Normal RFQ */}
                          {draftEmail && (
                            <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-yellow-700">
                                  <Mail className="w-4 h-4" />
                                  <span>Draft ready to send to {draftEmail.vendor_name}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={() => setDraftEmail(null)}>Cancel</Button>
                                  <Button size="sm" onClick={handleConfirmSendEmail} disabled={aiChatLoading} className="bg-green-600 hover:bg-green-700">
                                    {aiChatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Send</>}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Chat Input */}
                          <div className="p-4 border-t dark:border-gray-700">
                            <div className="flex gap-2">
                              <AIChatInput
                                ref={aiChatInputRef}
                                onSend={handleRepliesAiChat}
                                disabled={aiChatLoading}
                                placeholder="Ask AI..."
                              />
                              <Button
                                className="bg-purple-600 hover:bg-purple-700"
                                onClick={() => aiChatInputRef.current?.triggerSend()}
                                disabled={aiChatLoading}
                              >
                                {aiChatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {pdfFilename}
                </h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setPdfViewerOpen(false); setPdfData(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* PDF Content */}
            <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-red-500 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Loading PDF...</p>
                  </div>
                </div>
              ) : pdfData ? (
                <iframe
                  src={`data:application/pdf;base64,${pdfData}`}
                  className="w-full h-[75vh]"
                  title={pdfFilename}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-red-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                    <p>Failed to load PDF</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => { setPdfViewerOpen(false); setPdfData(null); }}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PO Confirmation Modal */}
      {showPOConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Purchase Order Creation
                </h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPOConfirmModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Items:</h4>
                <div className="space-y-2">
                  {Object.values(selectedQuotes).map((selection, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {selection.item_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Vendor: {selection.vendor_name}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(selection.unit_price)} x {selection.quantity}
                        </div>
                        <div className="text-sm text-green-600 font-semibold">
                          {formatCurrency(selection.total_price)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t dark:border-gray-700 pt-4">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span className="text-gray-700 dark:text-gray-300">Total Amount:</span>
                  <span className="text-purple-600">{formatCurrency(getPOSummary().totalAmount)}</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {getPOSummary().vendors.length} vendor(s): {getPOSummary().vendors.join(', ')}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowPOConfirmModal(false)}>
                Cancel
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={executeCreatePO}
                disabled={creatingPO}
              >
                {creatingPO ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Create Purchase Order
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default RFQWorkflowDetail;
